/**
 * Company Directory — "who works here" view for every teammate.
 *
 * Distinct from /employees (HR admin roster). Backend strips sensitive
 * fields, so this page is safe to expose to all roles.
 */
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { LayoutGrid, List, Search, Mail, Phone, MapPin, Users, SearchX, GitBranch } from "lucide-react";
import { fullName, pageTransition } from "@/lib/utils";
import { PageHeader } from "@/components/layout/PageHeader";
import { Card } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Button } from "@/components/ui/Button";
import { Avatar } from "@/components/ui/Avatar";
import { Badge } from "@/components/ui/Badge";
import { EmptyState } from "@/components/shared/EmptyState";
import { useDebounce } from "@/hooks/useDebounce";
import { useToast } from "@/hooks/useToast";
import {
  listDirectory,
  listDirectoryLocations,
  type DirectoryCard,
  type DirectoryEmploymentType,
} from "@/api/directory";
import { listDepartments } from "@/api/organizations";
import type { DepartmentFull } from "@/types/organization";
import type { PaginatedResponse } from "@/types/common";
import { EMPLOYMENT_TYPE_LABELS } from "@/lib/constants";
import { DirectoryProfilePanel } from "@/components/directory/DirectoryProfilePanel";
import { WhosOutWidget } from "@/components/directory/WhosOutWidget";
import { useNavigate } from "react-router-dom";

type ViewMode = "grid" | "list";

export function DirectoryPage() {
  const { addToast } = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [view, setView] = useState<ViewMode>("grid");
  const [search, setSearch] = useState("");
  const [deptFilter, setDeptFilter] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState<"" | DirectoryEmploymentType>("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState<PaginatedResponse<DirectoryCard> | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [departments, setDepartments] = useState<DepartmentFull[]>([]);
  const [locations, setLocations] = useState<string[]>([]);
  const debouncedSearch = useDebounce(search);

  const selectedPerson = searchParams.get("person");
  const setSelectedPerson = (id: string | null) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (id) next.set("person", id);
      else next.delete("person");
      return next;
    });
  };

  useEffect(() => {
    listDepartments()
      .then(setDepartments)
      .catch(() => {});
    listDirectoryLocations()
      .then(setLocations)
      .catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    listDirectory(page, 24, {
      search: debouncedSearch || undefined,
      department_id: deptFilter || undefined,
      location: locationFilter || undefined,
      employment_type: typeFilter || undefined,
    })
      .then(setData)
      .catch(() => addToast("error", "Failed to load directory"))
      .finally(() => setLoading(false));
  }, [page, debouncedSearch, deptFilter, locationFilter, typeFilter, addToast]);

  const items = data?.items ?? [];
  const hasFilters = !!(
    debouncedSearch ||
    deptFilter ||
    locationFilter ||
    typeFilter
  );
  const clearFilters = () => {
    setSearch("");
    setDeptFilter("");
    setLocationFilter("");
    setTypeFilter("");
    setPage(1);
  };

  const locationOptions = useMemo(
    () => [
      { value: "", label: "All locations" },
      ...locations.map((l) => ({ value: l, label: l })),
    ],
    [locations]
  );

  return (
    <motion.div {...pageTransition}>
      <PageHeader
        title="Directory"
        subtitle={
          data
            ? `${data.total} ${data.total === 1 ? "person" : "people"}`
            : "Loading…"
        }
        actions={
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/employees/org-chart")}
          >
            <GitBranch className="h-4 w-4" />
            Org chart
          </Button>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
        {/* Main column */}
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex flex-col sm:flex-row flex-wrap gap-3">
            <div className="flex-1 min-w-[200px] max-w-md">
              <Input
                placeholder="Search name, title, email…"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                icon={<Search className="h-4 w-4" />}
              />
            </div>
            <Select
              value={deptFilter}
              onChange={(e) => {
                setDeptFilter(e.target.value);
                setPage(1);
              }}
              options={[
                { value: "", label: "All departments" },
                ...departments.map((d) => ({ value: d.id, label: d.name })),
              ]}
            />
            <Select
              value={locationFilter}
              onChange={(e) => {
                setLocationFilter(e.target.value);
                setPage(1);
              }}
              options={locationOptions}
            />
            <Select
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter(e.target.value as "" | DirectoryEmploymentType);
                setPage(1);
              }}
              options={[
                { value: "", label: "All types" },
                ...Object.entries(EMPLOYMENT_TYPE_LABELS).map(([v, l]) => ({
                  value: v,
                  label: l,
                })),
              ]}
            />
            <div className="ml-auto flex items-center gap-1 rounded-lg border border-border p-0.5">
              <button
                type="button"
                onClick={() => setView("grid")}
                aria-label="Grid view"
                className={`rounded p-1.5 ${
                  view === "grid"
                    ? "bg-accent/10 text-accent"
                    : "text-text-muted hover:text-text-primary"
                }`}
              >
                <LayoutGrid className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setView("list")}
                aria-label="List view"
                className={`rounded p-1.5 ${
                  view === "list"
                    ? "bg-accent/10 text-accent"
                    : "text-text-muted hover:text-text-primary"
                }`}
              >
                <List className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Results */}
          {loading ? (
            <Card>
              <p className="text-sm text-text-muted text-center py-8">
                Loading directory…
              </p>
            </Card>
          ) : items.length === 0 ? (
            <Card>
              <EmptyState
                icon={hasFilters ? SearchX : Users}
                title={
                  hasFilters ? "No employees match your search" : "No employees yet"
                }
                description={
                  hasFilters
                    ? "Try a different search or clear a filter."
                    : "Once teammates are added they'll show up here."
                }
                action={
                  hasFilters
                    ? { label: "Clear filters", onClick: clearFilters }
                    : undefined
                }
              />
            </Card>
          ) : view === "grid" ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setSelectedPerson(p.id)}
                  className="text-left bg-surface rounded-xl border border-border hover:border-accent/50 transition-colors p-4 flex flex-col gap-3"
                >
                  <div className="flex items-center gap-3">
                    <Avatar
                      firstName={p.first_name}
                      lastName={p.last_name}
                      src={p.avatar_url}
                      size="lg"
                    />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-text-primary truncate">
                        {fullName(p.first_name, p.last_name, p.preferred_name)}
                      </p>
                      <p className="text-xs text-text-muted truncate">
                        {p.title}
                      </p>
                      {p.department && (
                        <p className="text-xs text-text-muted truncate">
                          {p.department.name}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="space-y-1 text-xs text-text-muted">
                    {p.work_email && (
                      <div className="flex items-center gap-2 truncate">
                        <Mail className="h-3.5 w-3.5 flex-shrink-0" />
                        <span className="truncate">{p.work_email}</span>
                      </div>
                    )}
                    {p.phone && (
                      <div className="flex items-center gap-2">
                        <Phone className="h-3.5 w-3.5 flex-shrink-0" />
                        <span>{p.phone}</span>
                      </div>
                    )}
                    {p.work_location && (
                      <div className="flex items-center gap-2">
                        <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                        <span className="capitalize">{p.work_location}</span>
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <Card>
              <div className="divide-y divide-border">
                {items.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setSelectedPerson(p.id)}
                    className="w-full text-left py-3 flex items-center gap-3 hover:bg-background/50 -mx-2 px-2 rounded transition-colors"
                  >
                    <Avatar
                      firstName={p.first_name}
                      lastName={p.last_name}
                      src={p.avatar_url}
                      size="sm"
                    />
                    <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-4 gap-2 items-center">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-text-primary truncate">
                          {fullName(p.first_name, p.last_name, p.preferred_name)}
                        </p>
                        <p className="text-xs text-text-muted truncate">
                          {p.title}
                        </p>
                      </div>
                      <p className="text-xs text-text-muted truncate">
                        {p.department?.name || "—"}
                      </p>
                      <p className="text-xs text-text-muted truncate hidden sm:block">
                        {p.work_email || "—"}
                      </p>
                      <div className="hidden sm:block">
                        {p.work_location && (
                          <Badge variant="default">{p.work_location}</Badge>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </Card>
          )}

          {/* Pagination */}
          {data && data.total_pages > 1 && (
            <div className="flex items-center justify-between pt-2">
              <p className="text-xs text-text-muted">
                Page {data.page} of {data.total_pages}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={page >= data.total_pages}
                  onClick={() =>
                    setPage((p) => Math.min(data.total_pages, p + 1))
                  }
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar column */}
        <aside className="space-y-4">
          <WhosOutWidget />
        </aside>
      </div>

      <DirectoryProfilePanel
        employeeId={selectedPerson}
        onClose={() => setSelectedPerson(null)}
      />
    </motion.div>
  );
}
